import React from 'react'
import { useLocation, useNavigate, useParams, useOutletContext } from 'react-router-dom'

// components
import { DashboardSkeleton, PublicPageSkeleton, AuthSkeleton } from './components'

const DashboardHome = React.lazy(() =>
  import(/* webpackChunkName: "DashboardHome" */ './scenes/Dashboard/DashboardHome')
)

const DashboardOpps = React.lazy(() =>
  import(/* webpackChunkName: "DashboardOpps" */ './scenes/Dashboard/DashboardOpps')
)

const DashboardOppsNew = React.lazy(() =>
  import(/* webpackChunkName: "DashboardOppsNew" */ './scenes/Dashboard/DashboardOppsNew')
)
const DashboardRoundsNew = React.lazy(() =>
  import(/* webpackChunkName: "DashboardRoundsNew" */ './scenes/Dashboard/DashboardRoundsNew')
)
const DashboardWorkshopsNew = React.lazy(() =>
  import(/* webpackChunkName: "DashboardWorkshopsNew" */ './scenes/Dashboard/DashboardWorkshopsNew')
)

const DashboardOpp = React.lazy(() =>
  import(/* webpackChunkName: "DashboardOpp" */ './scenes/Dashboard/DashboardOpp')
)
const DashRound = React.lazy(() =>
  import(/* webpackChunkName: "DashboardRound" */ './scenes/Dashboard/DashboardRound')
)
const DashWorkshop = React.lazy(() =>
  import(/* webpackChunkName: "DashboardWorkshop" */ './scenes/Dashboard/DashboardWorkshop')
)

const DashOppActivity = React.lazy(() =>
  import(/* webpackChunkName: "DashOppActivity" */ './scenes/Dashboard/DashboardOppActivity')
)

const DashOppPersonActivity = React.lazy(() =>
  import(
    /* webpackChunkName: "DashOppPersonActivity" */ './scenes/Dashboard/DashboardOppPersonActivity'
  )
)

const DashPersonActivity = React.lazy(() =>
  import(/* webpackChunkName: "DashPersonActivity" */ './scenes/Dashboard/DashboardPersonActivity')
)

const DashUpdateMembership = React.lazy(() =>
  import(
    /* webpackChunkName: "DashUpdateMembership" */ './scenes/Dashboard/DashboardUpdatePersonMembership'
  )
)

const DashPlatformEvents = React.lazy(() =>
  import(
    /* webpackChunkName: "DashboardPlatformEvents" */ './scenes/Dashboard/DashboardPlatformEvents'
  )
)

const DashEmailActivity = React.lazy(() =>
  import(
    /* webpackChunkName: "DashboardEmailActivity" */ './scenes/Dashboard/DashboardEmailActivity'
  )
)

const DashStories = React.lazy(() =>
  import(/* webpackChunkName: "DashboardStories" */ './scenes/Dashboard/DashboardStories')
)
const DashStoryNew = React.lazy(() =>
  import(/* webpackChunkName: "DashboardStoryNew" */ './scenes/Dashboard/DashboardStoryNew')
)

const DashStory = React.lazy(() =>
  import(/* webpackChunkName: "DashboardStory" */ './scenes/Dashboard/DashboardStory')
)

const DashboardCamps = React.lazy(() =>
  import(/* webpackChunkName: "DashboardCamps" */ './scenes/Dashboard/DashboardCamps')
)

const DashboardCampNew = React.lazy(() =>
  import(/* webpackChunkName: "DashboardCampNew" */ './scenes/Dashboard/DashboardCampNew')
)

const DashboardCamp = React.lazy(() =>
  import(/* webpackChunkName: "DashboardCamp" */ './scenes/Dashboard/DashboardCamp')
)

const DashNotFound = React.lazy(() =>
  import(/* webpackChunkName: "DashboardNotFound" */ './scenes/Dashboard/DashboardNotFound')
)

const DashAccount = React.lazy(() =>
  import(/* webpackChunkName: "DashboardAccount" */ './scenes/Dashboard/DashboardAccount')
)

const DashUserAccountPrefs = React.lazy(() =>
  import(
    /* webpackChunkName: "DashboardUserAccountPrefs" */ './scenes/Dashboard/Account/UserNotificationPreferences'
  )
)

const DashInstitutions = React.lazy(() =>
  import(/* webpackChunkName: "DashInstitutions" */ './scenes/Dashboard/DashboardInstitutions')
)
const DashInstitutionNew = React.lazy(() =>
  import(/* webpackChunkName: "DashInstitutionNew" */ './scenes/Dashboard/DashboardInstitutionNew')
)
const DashInstitution = React.lazy(() =>
  import(/* webpackChunkName: "DashInstitution" */ './scenes/Dashboard/DashboardInstitution')
)

const DashboardOrgs = React.lazy(() =>
  import(/* webpackChunkName: "DashboardOrgs" */ './scenes/Dashboard/DashboardOrgs')
)
const DashboardOrgNew = React.lazy(() =>
  import(/* webpackChunkName: "DashboardOrgNew" */ './scenes/Dashboard/DashboardOrgNew')
)
const DashboardOrg = React.lazy(() =>
  import(/* webpackChunkName: "DashboardOrg" */ './scenes/Dashboard/DashboardOrg')
)
const DashboardOrgMembers = React.lazy(() =>
  import(/* webpackChunkName: "DashOrgMembers" */ './scenes/Dashboard/DashboardOrganisationMembers')
)
const DashSchoolLearners = React.lazy(() =>
  import(/* webpackChunkName: "DashSchoolLearners" */ './scenes/Dashboard/DashboardSchoolLearners')
)

const DashboardPeopleNew = React.lazy(() =>
  import(/* webpackChunkName: "DashboardPeopleNew" */ './scenes/Dashboard/DashboardPeopleNew')
)

const AsyncDashboardPeopleIndex = React.lazy(() =>
  import(/* webpackChunkName: "DashPeopleIndex" */ './scenes/Dashboard/DashboardPeopleIndex')
)

const AsyncTerms = React.lazy(() => import(/* webpackChunkName: "Terms" */ './scenes/Terms/Terms'))
const AsyncAboutUs = React.lazy(() =>
  import(/* webpackChunkName: "AboutUs" */ './scenes/AboutUs/new')
)
const AsyncHomePage = React.lazy(() => import(/* webpackChunkName: "Home" */ './scenes/Home'))

const AsyncPrivacyPolicy = React.lazy(() =>
  import(/* webpackChunkName: "Terms" */ './scenes/PrivacyPolicy')
)

const AsyncNotFound = React.lazy(() =>
  import(/* webpackChunkName: "NotFound" */ './scenes/NotFound/NotFound')
)

const AsyncLogin = React.lazy(() => import(/* webpackChunkName: "Login" */ './scenes/Login/Login'))

const AsyncEmailConfirmation = React.lazy(() =>
  import(/* webpackChunkName: "EmailConfirmation" */ './scenes/Login/EmailConfirmation')
)

const AsyncProbation = React.lazy(() =>
  import(/* webpackChunkName: "Probation" */ './scenes/Login/Probation')
)

const AsyncPasswordReset = React.lazy(() =>
  import(/* webpackChunkName: "PasswordReset" */ './scenes/PasswordReset/PasswordReset')
)

const AsyncMagicLogin = React.lazy(() =>
  import(/* webpackChunkName: "MagicLogin" */ './scenes/PasswordReset/MagicLogin')
)

const AsyncPasswordSetup = React.lazy(() =>
  import(/* webpackChunkName: "PasswordSetup" */ './scenes/PasswordSetup/PasswordSetup')
)
const AsyncOnboardMentor = React.lazy(() =>
  import(/* webpackChunkName: "OnboardMentor" */ './scenes/Onboarding/OnboardMentor')
)
const AsyncOnboardPrincipal = React.lazy(() =>
  import(/* webpackChunkName: "OnboardPrincipal" */ './scenes/Onboarding/OnboardPrincipal')
)

const AsyncCampaign = React.lazy(() =>
  import(/* webpackChunkName: "Campaign" */ './scenes/Campaign/Campaign')
)

const AsyncOpportunity = React.lazy(() =>
  import(/* webpackChunkName: "Opportunity" */ './scenes/Opportunities/Opportunity')
)

const AsyncOrganisation = React.lazy(() =>
  import(/* webpackChunkName: "Organisation" */ './scenes/Organisations/Organisation')
)

const AsyncClaimOrganisation = React.lazy(() =>
  import(/* webpackChunkName: "ClaimOrganisation" */ './scenes/Organisations/ClaimOrganisation')
)

const AsyncClaimOrganisationRequest = React.lazy(() =>
  import(
    /* webpackChunkName: "ClaimOrganisationRequest" */ './scenes/Organisations/ClaimOrganisationRequest'
  )
)

const AsyncUser = React.lazy(() => import(/* webpackChunkName: "User" */ './scenes/Users/User'))

const AsyncHostFeedback = React.lazy(() =>
  import(/* webpackChunkName: "HostFeedback" */ './scenes/Feedback/HostFeedback')
)

const AsyncVolunteerFeedback = React.lazy(() =>
  import(/* webpackChunkName: "VolunteerFeedback" */ './scenes/Feedback/VolunteerFeedback')
)

const AsyncSpecs = React.lazy(() => import(/* webpackChunkName: "Specs" */ './scenes/Misc/Specs'))
const AsyncValues = React.lazy(() =>
  import(/* webpackChunkName: "Values" */ './scenes/MarketingPages/Values')
)

const AsyncNonProfitSearch = React.lazy(() =>
  import(/* webpackChunkName: "NonProfitSearch" */ './scenes/Search/NonProfitSearch')
)
const AsyncOppSearch = React.lazy(() =>
  import(/* webpackChunkName: "OppSearch" */ './scenes/Search/OppSearch')
)
const AsyncOrgSearch = React.lazy(() =>
  import(/* webpackChunkName: "OrgSearch" */ './scenes/Search/OrgSearch')
)

const AsyncJoinBrowniePoints = React.lazy(() =>
  import(/* webpackChunkName: "JoinBrowniePoints" */ './scenes/Onboarding/JoinBrowniePoints')
)

const AsyncCompleteOnboarding = React.lazy(() =>
  import(/* webpackChunkName: "CompleteOnboarding" */ './scenes/Onboarding/CompleteOnboarding')
)

const AsyncStories = React.lazy(() =>
  import(/* webpackChunkName: "Stories" */ './scenes/Stories/Stories')
)

function withOutletContext(Component) {
  return function OutletContextInjectedComponent(props) {
    const contextProps = useOutletContext()
    const location = useLocation()
    const navigate = useNavigate()
    const params = useParams()

    return (
      <Component {...props} {...contextProps} {...params} location={location} navigate={navigate} />
    )
  }
}

const withDashboardSkeleton = WrappedComponent => {
  // First, apply withOutletContext to get a component that has outlet context props
  const ComponentWithOutletContext = withOutletContext(WrappedComponent)

  // Then, return the component wrapped with DashboardSkeleton logic
  return props => (
    <React.Suspense fallback={<DashboardSkeleton />}>
      {/* Use ComponentWithOutletContext here to pass down outlet context props */}
      <ComponentWithOutletContext {...props} />
    </React.Suspense>
  )
}

const withPublicPageSkeleton = WrappedComponent => {
  // First, apply withOutletContext to get a component that has outlet context props
  const ComponentWithOutletContext = withOutletContext(WrappedComponent)

  // Then, return the component wrapped with DashboardSkeleton logic
  return props => (
    <React.Suspense fallback={<PublicPageSkeleton />}>
      {/* Use ComponentWithOutletContext here to pass down outlet context props */}
      <ComponentWithOutletContext {...props} />
    </React.Suspense>
  )
}

const withAuthSkeleton = WrappedComponent => props => (
  <React.Suspense fallback={<AuthSkeleton />}>
    <WrappedComponent {...props} />
  </React.Suspense>
)

///
///
/// Start of Growsmart-specific routes
///
///

const GsMentors = React.lazy(() =>
  import(/* webpackChunkName: "GsMentors" */ './scenes/Namespaced/Growsmart/DashboardMentors')
)

const GsRsvps = React.lazy(() =>
  import(/* webpackChunkName: "GsRsvps" */ './scenes/Namespaced/Growsmart/DashboardRsvps')
)

const GsRegistrations = React.lazy(() =>
  import(
    /* webpackChunkName: "GsRegistrations" */ './scenes/Namespaced/Growsmart/DashboardRegistrations'
  )
)

const GsManageRound = React.lazy(() =>
  import(
    /* webpackChunkName: "GsManageRound" */ './scenes/Namespaced/Growsmart/DashboardManageRound'
  )
)

const GsEnrolment = React.lazy(() =>
  import(/* webpackChunkName: "GsEnrolment" */ './scenes/Namespaced/Growsmart/DashboardEnrolment')
)
const GsEnrolments = React.lazy(() =>
  import(/* webpackChunkName: "GsEnrolments" */ './scenes/Namespaced/Growsmart/DashboardEnrolments')
)

const GsInviteDecline = React.lazy(() =>
  import(/* webpackChunkName: "GsInviteDecline" */ './scenes/Namespaced/Growsmart/GsInviteDecline')
)

const GsParentInfo = React.lazy(() =>
  import(/* webpackChunkName: "GsParentInfo" */ './scenes/Namespaced/Growsmart/GsParentInfo')
)

const GsInviteSchools = React.lazy(() =>
  import(
    /* webpackChunkName: "GsInviteSchools" */ './scenes/Namespaced/Growsmart/DashboardInviteSchools'
  )
)

const GsCompetitionOverview = React.lazy(() =>
  import(
    /* webpackChunkName: "GsInviteSchools" */ './scenes/Namespaced/Growsmart/DashboardCompetitionOverview'
  )
)

const AsyncDocsPage = React.lazy(() => import(/* webpackChunkName: "Docs" */ './scenes/Docs/Docs'))

const GsHardcopySubmissions = React.lazy(() =>
  import(
    /* webpackChunkName: "GsHardcopySubmissions" */ './scenes/Namespaced/Growsmart/DashboardHardcopySubmissions'
  )
)

const GsSponsorships = React.lazy(() =>
  import(
    /* webpackChunkName: "GsSponsorships" */ './scenes/Namespaced/Growsmart/DashboardSponsorships'
  )
)

const GsSponsorship = React.lazy(() =>
  import(
    /* webpackChunkName: "GsSponsorship" */ './scenes/Namespaced/Growsmart/DashboardSponsorship'
  )
)

const GsSponsorshipNew = React.lazy(() =>
  import(
    /* webpackChunkName: "GsSponsorshipNew" */ './scenes/Namespaced/Growsmart/DashboardSponsorshipNew'
  )
)

const GsScholarships = React.lazy(() =>
  import(
    /* webpackChunkName: "GsScholarships" */ './scenes/Namespaced/Growsmart/DashboardScholarships'
  )
)

const GsScholarship = React.lazy(() =>
  import(
    /* webpackChunkName: "GsScholarship" */ './scenes/Namespaced/Growsmart/DashboardScholarship'
  )
)

const GsScholarshipNew = React.lazy(() =>
  import(
    /* webpackChunkName: "GsScholarshipNew" */ './scenes/Namespaced/Growsmart/DashboardScholarshipNew'
  )
)

const GsScholarshipApplications = React.lazy(() =>
  import(
    /* webpackChunkName: "GsScholarshipsApplications" */ './scenes/Namespaced/Growsmart/DashboardScholarshipApplicationsList'
  )
)

const GsDashboardScholarshipApp = React.lazy(() =>
  import(
    /* webpackChunkName: "GsDashboardScholarshipApp" */ './scenes/Namespaced/Growsmart/DashboardScholarshipApplication'
  )
)

const GsScholarshipNewApplication = React.lazy(() =>
  import(
    /* webpackChunkName: "GsScholarshipNewApplication" */ './scenes/Namespaced/Growsmart/DashboardScholarshipNewApplication'
  )
)

export const GrowsmartMentors = withDashboardSkeleton(GsMentors)
export const GrowsmartRsvps = withDashboardSkeleton(GsRsvps)
export const GrowsmartRegistrations = withDashboardSkeleton(GsRegistrations)
export const GrowsmartManageRound = withDashboardSkeleton(GsManageRound)
export const GrowsmartEnrolments = withDashboardSkeleton(GsEnrolments)
export const GrowsmartEnrolment = withDashboardSkeleton(GsEnrolment)
export const GrowsmartSponsorships = withDashboardSkeleton(GsSponsorships)
export const GrowsmartSponsorship = withDashboardSkeleton(GsSponsorship)
export const GrowsmartSponsorshipNew = withDashboardSkeleton(GsSponsorshipNew)
export const GrowsmartScholarships = withDashboardSkeleton(GsScholarships)
export const GrowsmartScholarship = withDashboardSkeleton(GsScholarship)
export const GrowsmartScholarshipNew = withDashboardSkeleton(GsScholarshipNew)
export const GrowsmartScholarshipNewApplication = withDashboardSkeleton(GsScholarshipNewApplication)
export const GrowsmartScholarshipApplications = withDashboardSkeleton(GsScholarshipApplications)
export const DashboardScholarshipApplication = withDashboardSkeleton(GsDashboardScholarshipApp)

export const GrowsmartParentInfo = withDashboardSkeleton(GsParentInfo)
export const GrowsmartInvitationDecline = withDashboardSkeleton(GsInviteDecline)
export const GrowsmartInviteSchools = withDashboardSkeleton(GsInviteSchools)
export const GrowsmartCompetitionOverview = withDashboardSkeleton(GsCompetitionOverview)
export const GrowsmartHardcopySubmissions = withDashboardSkeleton(GsHardcopySubmissions)

///
///
/// End of Growsmart-specific routes
///
///

export const Terms = withPublicPageSkeleton(AsyncTerms)
export const AboutUs = withPublicPageSkeleton(AsyncAboutUs)
export const HomePage = withPublicPageSkeleton(AsyncHomePage)
export const PrivacyPolicy = withPublicPageSkeleton(AsyncPrivacyPolicy)
export const NotFound = withPublicPageSkeleton(AsyncNotFound)
export const Campaign = withPublicPageSkeleton(AsyncCampaign)
export const EmailConfirmation = withPublicPageSkeleton(AsyncEmailConfirmation)
export const Probation = withPublicPageSkeleton(AsyncProbation)
export const Opportunity = withPublicPageSkeleton(AsyncOpportunity)
export const Organisation = withPublicPageSkeleton(AsyncOrganisation)
export const ClaimOrganisation = withPublicPageSkeleton(AsyncClaimOrganisation)
export const ClaimOrganisationRequest = withPublicPageSkeleton(AsyncClaimOrganisationRequest)
export const User = withPublicPageSkeleton(AsyncUser)
export const HostFeedback = withPublicPageSkeleton(AsyncHostFeedback)
export const VolunteerFeedback = withPublicPageSkeleton(AsyncVolunteerFeedback)
export const Specs = withPublicPageSkeleton(AsyncSpecs)

export const NonProfitSearch = withPublicPageSkeleton(AsyncNonProfitSearch)
export const OppSearch = withPublicPageSkeleton(AsyncOppSearch)
export const OrgSearch = withPublicPageSkeleton(AsyncOrgSearch)
export const Values = withPublicPageSkeleton(AsyncValues)

export const JoinBrowniePoints = withAuthSkeleton(AsyncJoinBrowniePoints)
export const CompleteOnboarding = withDashboardSkeleton(AsyncCompleteOnboarding)

export const Stories = withDashboardSkeleton(AsyncStories)

export const Login = withAuthSkeleton(AsyncLogin)
export const PasswordReset = withAuthSkeleton(AsyncPasswordReset)
export const MagicLogin = withAuthSkeleton(AsyncMagicLogin)
export const PasswordSetup = withAuthSkeleton(AsyncPasswordSetup)
export const OnboardMentor = withAuthSkeleton(AsyncOnboardMentor)
export const OnboardPrincipal = withAuthSkeleton(AsyncOnboardPrincipal)

export const Dashboard = withDashboardSkeleton(DashboardHome)
export const DashboardOpportunities = withDashboardSkeleton(DashboardOpps)
export const DashboardNewOpportunities = withDashboardSkeleton(DashboardOppsNew)
export const DashboardNewWorkshops = withDashboardSkeleton(DashboardWorkshopsNew)
export const DashboardNewRounds = withDashboardSkeleton(DashboardRoundsNew)
export const DashboardOpportunity = withDashboardSkeleton(DashboardOpp)
export const DashboardRound = withDashboardSkeleton(DashRound)
export const DashboardWorkshop = withDashboardSkeleton(DashWorkshop)
export const DashboardOppActivity = withDashboardSkeleton(DashOppActivity)
export const DashboardOppPersonActivity = withDashboardSkeleton(DashOppPersonActivity)
export const DashboardPersonActivity = withDashboardSkeleton(DashPersonActivity)
export const DashboardUpdateMembership = withDashboardSkeleton(DashUpdateMembership)
export const DashboardCampaign = withDashboardSkeleton(DashboardCamp)
export const DashboardCampaigns = withDashboardSkeleton(DashboardCamps)
export const DashboardNewCampaign = withDashboardSkeleton(DashboardCampNew)
export const DashboardOrganization = withDashboardSkeleton(DashboardOrg)
export const DashboardOrganisationMembers = withDashboardSkeleton(DashboardOrgMembers)
export const DashboardSchoolLearners = withDashboardSkeleton(DashSchoolLearners)
export const DashboardInstitutionNew = withDashboardSkeleton(DashInstitutionNew)
export const DashboardInstitutions = withDashboardSkeleton(DashInstitutions)
export const DashboardInstitution = withDashboardSkeleton(DashInstitution)
export const DashboardOrganizations = withDashboardSkeleton(DashboardOrgs)
export const DashboardNewOrganization = withDashboardSkeleton(DashboardOrgNew)
export const DashboardNewPeople = withDashboardSkeleton(DashboardPeopleNew)
export const DashboardPeopleIndex = withDashboardSkeleton(AsyncDashboardPeopleIndex)
export const DashboardAccount = withDashboardSkeleton(DashAccount)
export const DashboardUserAccountPrefs = withDashboardSkeleton(DashUserAccountPrefs)
export const DashboardNotFound = withDashboardSkeleton(DashNotFound)
export const DashboardPlatformEvents = withDashboardSkeleton(DashPlatformEvents)
export const DashboardEmailActivity = withDashboardSkeleton(DashEmailActivity)
export const DashboardStories = withDashboardSkeleton(DashStories)
export const DashboardStory = withDashboardSkeleton(DashStory)
export const DashboardStoryNew = withDashboardSkeleton(DashStoryNew)
export const DocsPage = withPublicPageSkeleton(AsyncDocsPage)
